import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from '@/components/shadcn/card';
import { Button } from '@/components/shadcn/button';
import { Input } from '@/components/shadcn/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/shadcn/select';
import { Alert, AlertTitle, AlertDescription } from '@/components/shadcn/alert';
import { AlertCircle, X, PlusCircle } from 'lucide-react';
import { post } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { verifyToken } from '../../services/authService';
import { ONBOARDING_STEPS } from '../../utils/constants/constants';

const YEAR_GROUPS = ['Year 2', 'Year 3', 'Year 4', 'Year 5', 'Year 6'];

const PLAN_DETAILS = {
  platform_only: { displayName: 'Platform Single Plan', priceMonthly: 20, maxChildren: 1 },
  platform_only_family: { displayName: 'Platform Family Plan', priceMonthly: 30, maxChildren: 2 },
};

// Platform tops out at 2 children (Single vs Family). The tier is derived from how many children
// they add — the server (getPlanForUserOnboarding) does the same, so billing matches the count.
const PLATFORM_MAX_CHILDREN = 2;

const makeEmptyChild = () => ({ name: '', yearGroup: '', dateOfBirth: '', gender: '' });

const isChildComplete = (c) => !!(c.name && c.yearGroup && c.gender && c.dateOfBirth);

/**
 * Platform-only onboarding. Like the classroom flow, this is a no-card 7-day
 * trial — we just collect child details (no class selection) and start the
 * trial. The server assigns two random slots with no classroom.
 */
function PlatformOnboarding() {
  const { user, login } = useAuth();
  const navigate = useNavigate();

  const [children, setChildren] = useState([makeEmptyChild()]);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Tier (and price) follow the child count: 1 → Single (£20), 2 → Family (£30). selectedPlan only
  // routed them into this flow; the server derives the actual plan from the children they add.
  const planDetails =
    children.length >= 2 ? PLAN_DETAILS.platform_only_family : PLAN_DETAILS.platform_only;

  const updateChild = (idx, field, value) => {
    setChildren((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
    setError('');
  };

  const handleAddChild = () => {
    if (children.length < PLATFORM_MAX_CHILDREN) {
      setChildren([...children, makeEmptyChild()]);
    }
  };

  const handleRemoveChild = (idx) => {
    setChildren(children.filter((_, i) => i !== idx));
  };

  const validateDateOfBirth = (date) => new Date(date) < new Date();

  const handleSubmit = async () => {
    setError('');

    if (!children.every(isChildComplete)) {
      setError('Please complete every field for each child before continuing.');
      return;
    }
    if (children.some((c) => c.dateOfBirth && !validateDateOfBirth(c.dateOfBirth))) {
      setError('Date of birth must be in the past.');
      return;
    }

    setSubmitting(true);
    try {
      // 1. Save child details to onboarding progress (no slots, no holds).
      await post(
        `/onboarding/${user._id}/save`,
        {
          step: ONBOARDING_STEPS.SCHEDULE,
          data: {
            children: children.map((c) => ({
              name: c.name,
              yearGroup: c.yearGroup,
              dateOfBirth: c.dateOfBirth,
              gender: c.gender || null,
              scheduledLessons: [],
            })),
          },
        },
        true,
      );

      // 2. Start the trial (no card — matches the classroom flow).
      await post('/payments/create-trial-subscription', {}, true);

      // 3. Complete onboarding (server assigns random slots, no classroom).
      await post(`/onboarding/${user._id}/complete`, { userId: user._id }, true);

      // Refresh the user so accessStatus reflects the new subscription.
      const token = Cookies.get('token');
      const freshUserData = await verifyToken(token);
      login(freshUserData);

      navigate('/');
    } catch (err) {
      console.error('Platform onboarding failed:', err);
      setError('Something went wrong setting up your trial. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="flex flex-col items-center min-h-screen p-6">
        <div className="w-full max-w-3xl space-y-6">
          <div className="text-center">
            <h1 className="text-3xl font-bold">{planDetails.displayName}</h1>
            <p className="text-gray-600 mt-2">
              £{planDetails.priceMonthly}/month — start your 7-day free trial. No card details required.
            </p>
          </div>

          {children.map((child, idx) => (
            <Card key={idx} className="w-full relative">
              {idx > 0 && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute top-4 right-4 hover:bg-red-100"
                  onClick={() => handleRemoveChild(idx)}
                >
                  <X className="h-4 w-4 text-red-500" />
                  <span className="sr-only">Remove Child</span>
                </Button>
              )}
              <CardHeader>
                <CardTitle>Child {idx + 1} Details</CardTitle>
                <CardDescription>Enter your child's details to set up their learning.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Full Name</label>
                    <Input
                      value={child.name}
                      onChange={(e) => updateChild(idx, 'name', e.target.value)}
                      placeholder="Enter child's full name"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Year Group</label>
                    <Select value={child.yearGroup} onValueChange={(v) => updateChild(idx, 'yearGroup', v)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select year group" />
                      </SelectTrigger>
                      <SelectContent>
                        {YEAR_GROUPS.map((year) => (
                          <SelectItem key={year} value={year}>
                            {year}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Gender</label>
                    <Select value={child.gender} onValueChange={(v) => updateChild(idx, 'gender', v)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select gender" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Male">Male</SelectItem>
                        <SelectItem value="Female">Female</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Date of Birth</label>
                    <Input
                      type="date"
                      value={child.dateOfBirth}
                      onChange={(e) => updateChild(idx, 'dateOfBirth', e.target.value)}
                      max={new Date().toISOString().split('T')[0]}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}

          {children.length < PLATFORM_MAX_CHILDREN && (
            <Button onClick={handleAddChild} variant="outline" className="w-full">
              <PlusCircle className="mr-2 h-4 w-4" />
              Add Another Child
            </Button>
          )}

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <Button
            onClick={handleSubmit}
            disabled={submitting || !children.every(isChildComplete)}
            className="w-full py-6 text-lg bg-indigo-600 hover:bg-indigo-700"
          >
            {submitting ? 'Setting up your trial...' : 'Begin Your Free Trial!'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default PlatformOnboarding;
